import { Chip } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  isTypeCodeableConcept,
  isTypeCoding,
} from "../../../../types/codeableConcept";
import { createSlug } from "../../../../utils/createSlug";
import AutocompleteFreeOptions from "../../AutocompleteFreeOptions";
import { FreeOptionType } from "../../../../types/freeOptions";
import { buildTypeaheadFilterOptions } from "../typeaheadFilterOptions";
import { resolveTypeaheadOption } from "../typeaheadValueUtils";
import type { ResolvedTypeaheadSectionLabels } from "../typeaheadOptionSections";

type Props = {
  disabled?: boolean;
  freeOptions?: boolean;
  onItemSelection: (val: any, reason: string) => void;
  value?: any;
  options: any[];
  optionType: string;
  subset?: string[];
  loading?: boolean;
  fieldLabel: string;
  required?: boolean;
  setInputValue?: (value: string) => void;
  size?: "small" | "medium";
  variant?: "standard" | "filled" | "outlined";
  getOptionValue?: (a: any) => void;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  storeInitialValue: (value: any) => void;
  disableCloseOnSelect?: boolean;
  getOptionLabel?: (a: any) => string;
  getValueLabel?: (a: any) => string;
  isRemoteSearch?: boolean;
  searchQuery?: string;
  onDropdownOpen?: () => void;
  sectionLabels?: ResolvedTypeaheadSectionLabels;
};

function MultipleInput({
  disabled,
  freeOptions,
  onItemSelection,
  value,
  options,
  optionType,
  subset,
  loading,
  fieldLabel,
  required,
  setInputValue = () => {},
  size,
  variant,
  getOptionValue,
  isOptionEqualToValue,
  storeInitialValue,
  disableCloseOnSelect,
  getOptionLabel,
  getValueLabel,
  isRemoteSearch = false,
  searchQuery = "",
  onDropdownOpen,
  sectionLabels,
}: Props) {
  const findValueInOptions = (value, options, optionType) => {
    if (!value || !options?.length) return undefined;

    if (optionType === "snomed") {
      return options.find((o) =>
        isTypeCodeableConcept(value)
          ? o.snomedCID === value?.coding[0].code
          : isTypeCoding(value)
            ? o.snomedCID === value?.code
            : o.snomedCID === value?.identifier,
      );
    }

    return options.find((o) =>
      isTypeCodeableConcept(value)
        ? o.code === value?.coding[0].code
        : isTypeCoding(value)
          ? o.code === value?.code
          : o.code === value?.identifier,
    );
  };

  const resolveMultipleValues = (value, options, optionType) => {
    if (!value?.length) return [];

    return value
      .map((item) =>
        resolveTypeaheadOption(
          item,
          options,
          optionType,
          (entry, availableOptions) =>
            findValueInOptions(entry, availableOptions, optionType),
        ),
      )
      .filter((item) => item != null);
  };

  const [componentValue, setComponentValue] = useState<any>(() =>
    value?.length ? resolveMultipleValues(value, options, optionType) : [],
  );

  useEffect(() => {
    if (!value?.length) {
      setComponentValue([]);
      return;
    }

    const resolved = resolveMultipleValues(value, options, optionType);
    setComponentValue(resolved);
    storeInitialValue(resolved);
  }, [value, optionType]);

  const selectInputValue = (val, reason) => {
    if (val) {
      const v = getOptionValue ? val.map((v) => getOptionValue(v)) : val;
      onItemSelection(val, reason);
      setComponentValue(v);
    } else {
      onItemSelection(undefined, reason);
    }
  };

  const getLabel = (option) => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    if (optionType === "snomed") {
      return option.snomedFSN;
    }
    return option.display;
  };

  const filterOptions = useMemo(
    () =>
      buildTypeaheadFilterOptions({
        isRemoteSearch,
        freeOptions,
        getLabel,
        allowCreatableWhenResultsExist: false,
        createCreatableOption: (inputValue) =>
          optionType === "snomed"
            ? {
                snomedCID: createSlug(inputValue, "other"),
                snomedFSN: `Add "${inputValue}"`,
                isCreatable: true,
              }
            : {
                code: createSlug(inputValue, "other"),
                display: `Add "${inputValue}"`,
                isCreatable: true,
              },
      }),
    [isRemoteSearch, freeOptions, optionType, getOptionLabel],
  );

  const renderChipsTags = (value, getTagProps) =>
    value.map((option, index) => {
      const valueLabel = getValueLabel(option);
      const label = getLabel(option);
      return (
        <Chip
          label={valueLabel || label}
          {...getTagProps({ index })}
          deleteIcon={disabled ? <></> : null}
        />
      );
    });

  return (
    <AutocompleteFreeOptions
      multiple
      disabled={disabled}
      options={options}
      isRemoteSearch={isRemoteSearch}
      searchQuery={searchQuery}
      allowCreateOnBlur={freeOptions}
      type={[optionType, ...(subset || [])].join("-") as FreeOptionType}
      getOptionLabel={getLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      autoHighlight={freeOptions}
      onOpen={onDropdownOpen}
      sectionLabels={sectionLabels}
      onChange={(e, val, reason) => {
        selectInputValue(val, reason);
      }}
      loading={loading}
      onInputChange={(event, newInput) => {
        const trimmedInput = isEmpty(newInput.trim()) ? "" : newInput;
        setInputValue(trimmedInput);
      }}
      value={componentValue || []}
      renderTags={renderChipsTags}
      textFieldProps={{
        label: fieldLabel,
        required: required && componentValue?.length < 1,
        variant: variant || "outlined",
        size: size || "medium",
        InputLabelProps: { shrink: true, required },
      }}
      filterSelectedOptions
      filterOptions={filterOptions}
      id="tags-standard"
      disableCloseOnSelect={disableCloseOnSelect}
      size={size ?? "small"}
      getExistingOption={(val, op: Record<string, unknown>[]) =>
        op.find((o) => getLabel(o).toLowerCase() === val.toLowerCase())
      }
    />
  );
}

export default MultipleInput;
